/**
 * The composer. **INBOX scope item 13, the rendering half.**
 *
 * ⛔ STYLED TO BE SEEN AND TESTED, NOT TO LOOK FINISHED. This phase's rule: the
 * right panel and the thread are placeholders for tables CRM-BUILD has not
 * built, so spacing, type scale and hover polish would be re-done when the
 * layout moves. What styling MUST do is make each state distinguishable and
 * nothing invisible - which three checks already enforce a floor under
 * (`check-colors`, `check-css-classes`, `contrast.spec.ts`).
 */
import { useEffect, useRef, useState } from 'react'
import { canSend, canType, composerState } from './composer-state.js'

export type ComposerProps = {
  conversationId: string | null
  /** The newest outbound message's error code. See `ComposerInputs`. */
  lastErrorCode?: string | null
  /**
   * ⛔ THE THREAD FAILED TO LOAD. **You cannot compose into a conversation you
   * cannot see**, and the composer sat above "could not load this conversation"
   * offering a Send button until 2026-08-11.
   *
   * ⚠️ IT IS NOT `blocked`, AND THAT DISTINCTION IS THE WHOLE POINT. `blocked`
   * means *the send would be refused* - a shut window, an unusable channel - and
   * carries rule 4's reason. This means *we do not know what this conversation
   * is*, which is not a refusal: the send might work perfectly and we cannot
   * tell, and offering rule 4's wording for it would name a cause that is not
   * the cause.
   *
   * ⛔ AND IT DOES NOT UNMOUNT THE COMPOSER, because that reintroduces the jump
   * this pane was just fixed for: the region would resize as the error arrives.
   * The draft is also preserved, which matters most here - a load failure is
   * transient and the text is the only unreproducible thing on the screen.
   */
  threadUnavailable?: boolean
  /** Resolves when the write has landed. Rejection leaves the text in place. */
  onSend: (text: string, tab: ComposerTab) => Promise<void>
}

export type ComposerTab = 'message' | 'note'

export function Composer({
  conversationId,
  lastErrorCode = null,
  threadUnavailable = false,
  onSend,
}: ComposerProps): React.JSX.Element {
  /**
   * ┌─ ⛔ DRAFTS ARE PER CONVERSATION, AND UNTIL 2026-08-09 THEY WERE NOT ───────┐
   * │ `<Composer>` sits at a fixed position in `InboxShell`'s tree, so changing  │
   * │ `conversationId` RECONCILES it rather than remounting it - React keeps the │
   * │ instance and every piece of its state. **The text followed the agent.**    │
   * │                                                                          │
   * │ ⛔ MEASURED, NOT REASONED: type into A, switch to B, press Enter, and      │
   * │ `onSend` was called with A's sentence while B was open. That is not a      │
   * │ missing feature - it is a WRITE-PATH DEFECT that sends a half-typed        │
   * │ message to the wrong customer, and it is the only thing in this            │
   * │ component's defect list that could reach one.                             │
   * │                                                                          │
   * │ ⚠️ AND THE FEATURE THE PLAN ASKS FOR IS THE FIX. Item 13: *"DRAFTS        │
   * │ PERSIST PER CONVERSATION when navigating away. An agent checking another  │
   * │ thread mid-reply must not lose what they typed."* Keyed storage gives     │
   * │ both - the draft survives, and it survives ATTACHED TO ITS CONVERSATION.  │
   * │ The defect and the missing feature had one cause.                         │
   * └──────────────────────────────────────────────────────────────────────────┘
   *
   * ⚠️ IN MEMORY, NOT `localStorage`. A draft outliving the tab is a separate
   * decision with its own failure mode - a stale draft resurfacing days later
   * next to a conversation that has moved on - and nothing has decided it.
   * Registered as `composer-drafts-do-not-survive-reload` in `ops/deferrals.txt`.
   */
  const drafts = useRef(new Map<string, string>())
  const [text, setText] = useState('')
  const [tab, setTab] = useState<ComposerTab>('message')
  const [sending, setSending] = useState(false)
  const box = useRef<HTMLTextAreaElement | null>(null)

  /**
   * ⛔ THE PREVIOUS CONVERSATION'S DRAFT IS SAVED AND THE NEW ONE'S RESTORED, in
   * one effect, because they are one transition. Splitting them across a save on
   * unmount and a load on mount would need the component to unmount, which is
   * exactly what does not happen here.
   */
  const previous = useRef<string | null>(null)
  useEffect(() => {
    const was = previous.current
    if (was !== null && was !== conversationId) {
      // ⚠️ AN EMPTY DRAFT IS DELETED RATHER THAN STORED. Otherwise the map grows
      // one entry per conversation an agent merely clicks through, which on a
      // triage run is every row in the list.
      const current = box.current?.value ?? ''
      if (current.trim() === '') drafts.current.delete(was)
      else drafts.current.set(was, current)
    }
    previous.current = conversationId
    setText(conversationId === null ? '' : (drafts.current.get(conversationId) ?? ''))
  }, [conversationId])

  /**
   * ⛔ A NOTE IS NEVER BLOCKED BY A SEND CONDITION. It does not go to Meta, so
   * the window and the channel have nothing to say about it - and an agent
   * locked out of writing a note because WhatsApp is down is the opposite of
   * what they need at that moment.
   */
  const state =
    tab === 'note'
      ? composerState({ conversationId, sending, lastErrorCode: null })
      : composerState({ conversationId, sending, lastErrorCode })

  /**
   * ⛔ A LOAD FAILURE OUTRANKS EVERY SEND CONDITION, INCLUDING THE NOTE TAB'S
   * EXEMPTION. A note is never blocked by a channel or a window because neither
   * can affect it - but both of those are facts about SENDING, and this is a
   * fact about the CONVERSATION. If we could not load it, we do not know it is
   * the conversation the agent thinks it is, and writing a private note into the
   * wrong customer's record is the same class of error as sending to them.
   */
  const unavailable = conversationId !== null && threadUnavailable

  const commit = (): void => {
    const body = text.trim()
    if (body === '' || unavailable || !canSend(state)) return

    setSending(true)
    /**
     * ⛔ CLEARED ON COMMIT, AND FOR THE ACCIDENTAL DOUBLE-PRESS RATHER THAN FOR
     * IDEMPOTENCY. The key already handles the deliberate case: a second press
     * is a second commit and mints a second key, so it produces a second
     * message - which is what the agent asked for. What clearing prevents is a
     * fumbled double-tap sending the same sentence twice.
     *
     * ⚠️ AND IT IS RESTORED ON FAILURE. Losing what somebody wrote because the
     * network blipped is the one part of this a person cannot reproduce.
     */
    setText('')
    // ⚠️ AND THE STORED DRAFT GOES WITH IT. Clearing only the box would leave
    // the sent sentence in the map, so returning to this conversation would
    // reproduce a message the agent has already sent.
    if (conversationId !== null) drafts.current.delete(conversationId)
    void onSend(body, tab)
      .catch(() => setText(body))
      .finally(() => {
        setSending(false)
        /**
         * ⛔ FOCUS RETURNS HERE, AND **jsdom CANNOT SEE WHETHER IT WORKED.**
         * `document.activeElement` exists there, so a jsdom test would pass
         * while asserting only that a property was set - not that focus
         * survived a re-render and the browser's own management. That is the
         * presence-for-consequence substitution, and the convenient version is
         * the one that would get kept. **So this has no jsdom test at all**;
         * `tests/browser/` owns it, and until it is written the property is
         * UNVERIFIED rather than covered.
         */
        box.current?.focus()
      })
  }

  const blocked = state.kind === 'blocked' ? state.reason : null

  return (
    <div className="composer" data-testid="composer">
      {state.kind === 'noConversation' ? (
        // ⛔ NOT A DISABLED BOX. "Nothing is open" and "you cannot send here"
        // are different sentences, and a greyed textarea says the second.
        <p className="composer__idle" data-testid="composer-idle">
          Open a conversation to reply
        </p>
      ) : (
        <>
          <div className="composer__tabs" role="tablist">
            {(['message', 'note'] as const).map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={tab === t}
                className={`composer__tab${tab === t ? ' composer__tab--active' : ''}`}
                onClick={() => setTab(t)}
              >
                {t === 'message' ? 'Message' : 'Note'}
              </button>
            ))}
          </div>

          {unavailable && (
            /**
             * ⚠️ ITS OWN WORDING, NOT RULE 4'S. Rule 4's lines name a cause a
             * person can act on; this one has no cause we know. Saying "the
             * 24-hour window is closed" here would be confidently wrong, and
             * saying nothing would leave a live-looking Send button above an
             * error - which is what shipped.
             */
            <div className="composer__blocked" role="status" data-testid="composer-unavailable">
              <p className="composer__blocked-agent">
                This conversation could not be loaded, so nothing can be sent to it yet.
              </p>
              <p className="composer__blocked-diagnostic">
                Anything typed here is kept. Reload to try again.
              </p>
            </div>
          )}

          {blocked !== null && !unavailable && (
            /**
             * ⛔ BOTH LAYERS, AS EVERYWHERE RULE 4 APPLIES. The agent line and
             * the diagnostic, always visible, because nothing in this system
             * alerts on an `error` log line.
             */
            <div className="composer__blocked" role="status" data-testid="composer-blocked">
              <p className="composer__blocked-agent">{blocked.agent}</p>
              <p className="composer__blocked-diagnostic">{blocked.diagnostic}</p>
            </div>
          )}

          <textarea
            ref={box}
            className="composer__box"
            data-testid="composer-box"
            aria-label={tab === 'note' ? 'Internal note' : 'Message'}
            value={text}
            // ⚠️ TYPING STAYS ALLOWED WHILE BLOCKED. Refusing keystrokes would
            // discard what they had written, and a blocked send is often the
            // moment somebody most wants to keep their draft.
            disabled={!canType(state)}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              /**
               * ⛔ ENTER SENDS, SHIFT+ENTER DOES NOT. The build plan's wording,
               * and the modifier check is what separates them - without it a
               * multi-line note is unwritable, which is a whole tab broken.
               */
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                commit()
              }
            }}
          />

          <div className="composer__actions">
            {/*
              ⛔ THE IN-FLIGHT STATE IS NOT STYLED AS A REFUSAL. It reuses no
              class from `composer__blocked`, deliberately: a send that is
              working must not tell an agent something is wrong, and this fires
              on the HAPPY PATH, so it would cry wolf every single time.
            */}
            {state.kind === 'sending' && (
              <span className="composer__sending" data-testid="composer-sending">
                Sending
              </span>
            )}
            <button
              type="button"
              className="composer__send"
              data-testid="composer-send"
              // ⚠️ `unavailable` IS REPEATED HERE RATHER THAN LEFT TO `commit`.
              // Guarding only the handler leaves a LIVE-LOOKING Send button
              // above an error that does nothing when pressed - which is the
              // defect this state was added for, wearing a different face. The
              // first version of this fix had exactly that hole and the test
              // below found it.
              disabled={!canSend(state) || unavailable || text.trim() === ''}
              onClick={commit}
            >
              {tab === 'note' ? 'Save Note' : 'Send'}
            </button>
          </div>
        </>
      )}
    </div>
  )
}
